/**
 * Utility functions for timezone handling
 */

// Map user-friendly timezone names to IANA timezone IDs
export const TIMEZONE_MAP: Record<string, string> = {
  'Pacific': 'America/Los_Angeles',
  'Mountain': 'America/Denver',
  'Central': 'America/Chicago',
  'Eastern': 'America/New_York',
  'Alaska': 'America/Anchorage',
  'Hawaii-Aleutian': 'Pacific/Honolulu',
  'Atlantic': 'America/Halifax',
  'Newfoundland': 'America/St_Johns',
  'UTC': 'UTC'
};

// Map IANA timezone IDs to user-friendly names
export const IANA_TO_FRIENDLY_MAP: Record<string, string> = {
  'America/Los_Angeles': 'Pacific',
  'America/Vancouver': 'Pacific',
  'America/Tijuana': 'Pacific',
  'America/Denver': 'Mountain',
  'America/Edmonton': 'Mountain',
  'America/Boise': 'Mountain',
  'America/Phoenix': 'Mountain',
  'America/Chicago': 'Central',
  'America/Winnipeg': 'Central',
  'America/Regina': 'Central',
  'America/New_York': 'Eastern',
  'America/Toronto': 'Eastern',
  'America/Nassau': 'Eastern',
  'America/Montreal': 'Eastern',
  'America/Detroit': 'Eastern',
  'America/Anchorage': 'Alaska',
  'America/Juneau': 'Alaska',
  'America/Nome': 'Alaska',
  'Pacific/Honolulu': 'Hawaii-Aleutian',
  'America/Adak': 'Hawaii-Aleutian',
  'America/Halifax': 'Atlantic',
  'America/Glace_Bay': 'Atlantic',
  'America/Moncton': 'Atlantic',
  'America/St_Johns': 'Newfoundland',
  'UTC': 'UTC'
};

/**
 * Convert user-friendly timezone name to IANA timezone ID
 */
export function getIANATimezone(friendlyName: string): string {
  return TIMEZONE_MAP[friendlyName] || 'America/Denver'; // Default to Mountain
}

/**
 * Convert IANA timezone ID to user-friendly name
 */
export function getFriendlyTimezone(ianaId: string): string {
  return IANA_TO_FRIENDLY_MAP[ianaId] || 'Mountain'; // Default to Mountain
}

/**
 * Offset of a zone from UTC at an instant, in seconds, read from the platform's own tz database.
 */
function offsetSecondsAt(timeZoneId: string, at: Date): number {
  const label = new Intl.DateTimeFormat('en-US', { timeZone: timeZoneId, timeZoneName: 'longOffset' })
    .formatToParts(at)
    .find(p => p.type === 'timeZoneName')?.value ?? 'GMT';
  const match = /GMT([+-])(\d{2}):(\d{2})/.exec(label);
  if (!match) return 0;
  const seconds = Number(match[2]) * 3600 + Number(match[3]) * 60;
  return match[1] === '-' ? -seconds : seconds;
}

/**
 * Resolve the timezone at a coordinate.
 *
 * Resolved locally from a bundled boundary table rather than Google's Time Zone API: that web
 * service refuses referrer-restricted keys, and the only key a browser may hold is one. Offsets
 * are in seconds, matching the shape the Google response had.
 */
export async function getTimezoneFromCoordinates(
  latitude: number,
  longitude: number,
  at: Date = new Date()
): Promise<{
  timeZoneId: string;
  timeZoneName: string;
  rawOffset: number;
  dstOffset: number;
} | null> {
  try {
    // Loaded on demand so the boundary table only ships to the booking screens that need it.
    const { default: tzLookup } = await import('@photostructure/tz-lookup');
    const timeZoneId = tzLookup(latitude, longitude);

    const year = at.getUTCFullYear();
    const current = offsetSecondsAt(timeZoneId, at);
    const rawOffset = Math.min(
      offsetSecondsAt(timeZoneId, new Date(Date.UTC(year, 0, 1))),
      offsetSecondsAt(timeZoneId, new Date(Date.UTC(year, 6, 1)))
    );
    const timeZoneName = new Intl.DateTimeFormat('en-US', { timeZone: timeZoneId, timeZoneName: 'long' })
      .formatToParts(at)
      .find(p => p.type === 'timeZoneName')?.value ?? timeZoneId;

    return { timeZoneId, timeZoneName, rawOffset, dstOffset: current - rawOffset };
  } catch (error) {
    console.error('Error resolving timezone from coordinates:', error);
    return null;
  }
}

/**
 * Convert a date to a specific timezone
 * @param date - Date to convert
 * @param timeZoneId - IANA timezone ID (e.g., 'America/New_York')
 * @returns Date in the specified timezone
 */
export function convertDateToTimezone(date: Date, timeZoneId: string): Date {
  try {
    // Create a new date in the target timezone
    const utc = date.getTime() + (date.getTimezoneOffset() * 60000);
    const targetTime = new Date(utc);
    
    // Format the date in the target timezone
    const targetDate = new Date(targetTime.toLocaleString('en-US', { timeZone: timeZoneId }));
    
    return targetDate;
  } catch (error) {
    console.error('Error converting date to timezone:', error);
    return date; // Return original date if conversion fails
  }
}

/**
 * Get timezone offset in minutes for a specific timezone
 * @param timeZoneId - IANA timezone ID
 * @returns Timezone offset in minutes
 */
export function getTimezoneOffset(timeZoneId: string): number {
  try {
    const now = new Date();
    
    // Get the offset using Intl.DateTimeFormat for more accuracy
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZoneId,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(now);
    const targetDate = new Date(
      parseInt(parts.find(p => p.type === 'year')?.value || '0'),
      parseInt(parts.find(p => p.type === 'month')?.value || '1') - 1,
      parseInt(parts.find(p => p.type === 'day')?.value || '1'),
      parseInt(parts.find(p => p.type === 'hour')?.value || '0'),
      parseInt(parts.find(p => p.type === 'minute')?.value || '0'),
      parseInt(parts.find(p => p.type === 'second')?.value || '0')
    );
    
    // Calculate the offset in minutes
    const offsetMs = targetDate.getTime() - now.getTime();
    const offsetMinutes = Math.round(offsetMs / (1000 * 60));
    
    return offsetMinutes;
  } catch (error) {
    console.error('Error getting timezone offset:', error);
    return 0;
  }
}

/**
 * Convert a local time in a specific timezone to UTC
 * This is used for booking forms where we want to store the customer's local time as UTC
 * @param localDate - The local date/time in the customer's timezone
 * @param timeZoneId - IANA timezone ID (e.g., 'America/New_York')
 * @returns UTC date that represents the same moment in time
 */
export function convertLocalTimeToUTC(localDate: Date, timeZoneId: string): Date {
  try {
    // Get the timezone offset for the target timezone (in minutes)
    const timezoneOffset = getTimezoneOffset(timeZoneId);
    
    // Create a new date with the same components but adjusted for timezone
    const utcDate = new Date(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate(),
      localDate.getHours(),
      localDate.getMinutes() - timezoneOffset, // Subtract offset to get UTC
      0,
      0
    );
    
    return utcDate;
  } catch (error) {
    console.error('Error converting local time to UTC:', error);
    return localDate; // Return original date if conversion fails
  }
}

/**
 * Format a date in a specific timezone
 * @param date - Date to format
 * @param timeZoneId - IANA timezone ID
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDateInTimezone(
  date: Date, 
  timeZoneId: string, 
  options: Intl.DateTimeFormatOptions = {}
): string {
  try {
    return date.toLocaleString('en-US', { timeZone: timeZoneId, ...options });
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    return date.toString();
  }
}

/**
 * Convert a time string (HH:MM) from one timezone to another
 * @param timeStr - Time string in HH:MM format
 * @param fromTimezone - Source timezone (user-friendly name or IANA ID)
 * @param toTimezone - Target timezone (user-friendly name or IANA ID)
 * @returns Converted time string in HH:MM format
 */
export function convertTimeBetweenTimezones(timeStr: string, fromTimezone: string, toTimezone: string): string {
  try {
    // Convert friendly names to IANA IDs if needed
    const fromIANA = TIMEZONE_MAP[fromTimezone] || fromTimezone;
    const toIANA = TIMEZONE_MAP[toTimezone] || toTimezone;
    
    // Create a date object for today with the given time
    const today = new Date();
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
    
    // Convert to the target timezone
    const targetDate = new Date(date.toLocaleString('en-US', { timeZone: toIANA }));
    
    // Format back to HH:MM
    const targetHours = targetDate.getHours().toString().padStart(2, '0');
    const targetMinutes = targetDate.getMinutes().toString().padStart(2, '0');
    
    return `${targetHours}:${targetMinutes}`;
  } catch (error) {
    console.error('Error converting time between timezones:', error);
    return timeStr; // Return original time if conversion fails
  }
}

/**
 * Test function to verify timezone conversion logic
 * This can be called from the browser console for debugging
 */
export function testTimezoneConversion() {
  console.log('=== Testing Timezone Conversion ===');
  
  // Test case: June 30, 2024 at 8:00 AM in different timezones
  const testDate = new Date(2024, 5, 30, 8, 0); // June 30, 2024 8:00 AM
  const timezones = ['America/New_York', 'America/Los_Angeles', 'America/Denver', 'America/Chicago'];
  
  console.log('Original test date (local timezone):', testDate.toString());
  console.log('---');
  
  timezones.forEach(timezone => {
    const utcDate = convertLocalTimeToUTC(testDate, timezone);
    const timezoneOffset = getTimezoneOffset(timezone);
    
    console.log(`${timezone} (offset: ${timezoneOffset} minutes):`);
    console.log(`  Input: 8:00 AM ${timezone}`);
    console.log(`  UTC: ${utcDate.toISOString()}`);
    console.log(`  Formatted: ${formatDateInTimezone(utcDate, timezone, { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    })}`);
    console.log('---');
  });
  
  // Test the specific case that was causing issues
  console.log('=== Testing June 30th Issue ===');
  const june30Date = new Date(2024, 5, 30, 8, 0); // June 30, 2024 8:00 AM
  const customerTimezone = 'America/New_York';
  const utcResult = convertLocalTimeToUTC(june30Date, customerTimezone);
  
  console.log(`Customer books for: June 30, 2024 at 8:00 AM ${customerTimezone}`);
  console.log(`Stored as UTC: ${utcResult.toISOString()}`);
  console.log(`Should display as: ${formatDateInTimezone(utcResult, customerTimezone, { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  })}`);
  
  // Verify it's still June 30th
  const resultDate = new Date(utcResult);
  const isJune30 = resultDate.getUTCDate() === 30 && resultDate.getUTCMonth() === 5;
  console.log(`✅ Is it still June 30th? ${isJune30 ? 'YES' : 'NO'}`);
}

// Make the function available globally for easy console access
if (typeof window !== 'undefined') {
  (window as any).testTimezoneConversion = testTimezoneConversion;  
}